import DrawerListItem from "./drawer-list-item/DrawerListItem";
import {Grid, List} from "@mui/material";
import genie_logo from "../../../assets/images/logo.svg";
import {RoutePaths} from "../../../util/constants";
import {Link} from "react-router-dom";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import * as React from "react";
import {FC, useMemo, useState} from "react";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, QrCode} from "@mui/icons-material";
import {RootState, useAppSelector} from "../../../store";
import LanguageDropdown from "../language-dropdown/LanguageDropdown";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ScrollFader from "../scroll-fader/ScrollFader";
import {useTheme} from "@mui/material/styles";

interface IDrawerListItem {
    routeTo: string
    label: string
    icon: React.ReactNode
}

interface Props {
    drawerListItems: IDrawerListItem[]
    handleButtonClick?: () => void
    exploreMoreListItems?: IDrawerListItem[]
    toggleDrawer?: () => void
}

const DrawerList: FC<Props> = (props) => {
    const {t} = useTranslation()
    const isLoggedIn = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.id)
    const isAdmin = useAppSelector((state: RootState) => state.authReducer.loggedInUser.is_admin)
    const isClient = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.client_id)
    const [expandExploreMore, setExpandExploreMore] = useState(true)
    const theme = useTheme()

    const drawerActionButton = useMemo(() => {
        if (isLoggedIn) {
            if (isAdmin) {
                return <Button sx={{width: '100%', mr: '1em'}}
                               onClick={props.handleButtonClick}
                               startIcon={<QrCode/>}
                               variant="contained">
                    {t("scan_winner")}
                </Button>
            }
            if (isClient) {
                return <Button sx={{width: '100%', mr: '1em'}}
                               onClick={props.handleButtonClick}
                               startIcon={<QrCode/>}
                               variant="contained">
                    {t("validate_voucher")}
                </Button>
            }
        }
        return null
    }, [isAdmin, isClient, isLoggedIn, props.handleButtonClick, t])

    return (
        <Grid
            container
            flexDirection="column"
            height="100vh" // Full height of the viewport
            flexWrap="nowrap"
            sx={{
                overflow: "hidden", // Prevents unintended overflow
            }}
        >
            {/* Toolbar - Fixed Header */}
            <Toolbar sx={{justifyContent: 'center'}}>
                <Link to={RoutePaths.HOME} style={{display: 'flex', width: 'fit-content'}}>
                    <img src={genie_logo} width={100} alt="genie-logo"/>
                </Link>
            </Toolbar>
            <Divider sx={{backgroundColor: theme.palette.primary.main, height: '1px'}}/>

            {/* Scrollable Area */}
            <ScrollFader>
                <List>
                    {props.drawerListItems.map((di, idx) => (
                        <DrawerListItem
                            key={`drawer-list-item-${idx}`}
                            onNavigate={props.toggleDrawer}
                            routeTo={di.routeTo}
                            label={di.label}
                            icon={di.icon}
                        />
                    ))}
                </List>

                {!!props.exploreMoreListItems && (
                    <>
                        <Typography
                            variant="button"
                            color="primary"
                            fontWeight="bold"
                            px={2}
                            pt={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{cursor: 'pointer'}}
                            onClick={() => setExpandExploreMore((oldExpand) => !oldExpand)}
                        >
                            {t('explore_more')}
                            {expandExploreMore ? <KeyboardArrowDownOutlined/> : <KeyboardArrowUpOutlined/>}
                        </Typography>
                        {expandExploreMore && (
                            <List>
                                {props.exploreMoreListItems.map((di, idx) => (
                                    <DrawerListItem
                                        key={`drawer-list-item-${idx}`}
                                        onNavigate={props.toggleDrawer}
                                        routeTo={di.routeTo}
                                        label={di.label}
                                        icon={di.icon}
                                    />
                                ))}
                            </List>
                        )}
                    </>
                )}
            </ScrollFader>

            {/* Footer Section */}
            <Box
                sx={{
                    mt: 'auto',
                    p: "1em",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isAdmin || isClient ? 'space-between' : 'center',
                }}
            >
                {drawerActionButton}
                <LanguageDropdown/>
            </Box>
        </Grid>
    );
}


export default DrawerList;
